import { useMemo } from 'react';
import { FilterModel, IServerSideDatasource } from 'ag-grid-enterprise';
import { size } from 'lodash';

import {
    GetExecutionRequest,
    GetExecutionResponse,
    studioExecutionApi,
    StudioExecutionColumnTypes,
} from '@blockworks/platform/api/research/studio/execution';

import { useVisualizationOptionsContext } from '../../../../../../context';
import { VisualizationTableProps } from '../../models';
import { SYNTHETIC_GLOBAL_SEARCH_COL } from '../make-columns';

type UseVisualizationTableProps = Pick<VisualizationTableProps, 'data' | 'meta' | 'executionId' | 'options'> & {
    columnTypes: Record<string, StudioExecutionColumnTypes>;
    setColumnTypes: (columnTypes: Record<string, StudioExecutionColumnTypes>) => void;
};

type TableData = Omit<Partial<GetExecutionResponse['data']>, 'metadata'> & {
    metadata: Partial<GetExecutionResponse['data']['metadata']>;
};

const useDatasource = ({
    data: preloadedData,
    meta,
    executionId,
    columnTypes,
    setColumnTypes,
    options,
}: UseVisualizationTableProps) => {
    const { features } = useVisualizationOptionsContext();
    const datasource: IServerSideDatasource = useMemo(() => {
        return {
            getRows: async ({ success, fail, request }) => {
                const { endRow, startRow, sortModel, filterModel } = request;
                const pageSize = (endRow ?? 0) - (startRow ?? 0);
                const nextPage = (startRow ?? 0) / pageSize + 1;

                let order: GetExecutionRequest['order'] | undefined;
                const sortParams = sortModel[0];
                if (sortParams?.colId && sortParams?.sort) {
                    order = `${sortParams.colId},${sortParams.sort}`;
                }

                const globalSearch = (filterModel as FilterModel)?.[SYNTHETIC_GLOBAL_SEARCH_COL.colId];
                const preloadedDataSize = preloadedData?.length ?? 0;
                let tableData: TableData = { metadata: { rowCount: 0 } };

                if (
                    preloadedDataSize &&
                    nextPage < 2 &&
                    !globalSearch?.filter &&
                    !order &&
                    (preloadedDataSize >= options?.pageSize || preloadedDataSize >= (meta?.rowCount ?? 0))
                ) {
                    tableData['rows'] = preloadedData;
                    tableData.metadata.rowCount = meta?.rowCount ?? preloadedDataSize;
                } else {
                    try {
                        const res = await studioExecutionApi.useGetExecution.fetcher({
                            id: executionId,
                            page: nextPage,
                            limit: options?.pageSize ? +options?.pageSize : undefined,
                            order,
                            filter: globalSearch?.filter?.toLowerCase(),
                            sameOriginProxy: features.dangerouslyProxyRequests,
                        });
                        tableData = res.data;
                    } catch (error) {
                        console.error('Error fetching execution data for table:', error);
                        return fail();
                    }
                }

                /** if columnTypes is not already set, try to get them from the server or preloaded data */
                if (!size(columnTypes)) {
                    if (meta?.columnTypes) {
                        setColumnTypes(meta.columnTypes);
                    } else if (tableData?.metadata?.columnTypes) {
                        setColumnTypes(tableData.metadata.columnTypes);
                    }
                }

                if (tableData?.rows) {
                    return success({
                        rowData: tableData?.rows,
                        rowCount: tableData?.metadata?.rowCount || tableData?.rows?.length || 0,
                    });
                }

                console.error('Error using execution rows with table:', tableData);
                fail();
            },
        };
    }, [
        preloadedData,
        columnTypes,
        options?.pageSize,
        meta?.rowCount,
        meta?.columnTypes,
        executionId,
        features.dangerouslyProxyRequests,
        setColumnTypes,
    ]);

    return datasource;
};

export type { UseVisualizationTableProps };
export { useDatasource };
